import React, { useEffect, useRef } from "react";
import { TicketPurchaseApi } from "../services/TicketPurchaseApi";
import {
    authSlice,
    PURCHASED_ITINERARIES_DEFAULT,
    purchasedItinerariesSlice,
    usePersistedSlice,
    usePersistedSliceSelector,
    usePersistenceReady,
} from "../store/persistence";
import { buildPurchasedEntriesFromServer } from "../tools/purchasedItineraries.tools";

/**
 * Side-effect component syncing the purchased-itinerary slice with the session.
 *
 * - every transition to a valid `accessToken` (boot with session, login, cookie
 *   refresh after expiry) replaces the local list with the server truth
 *   (`GET /api/purchase/bookings` — committed bookings only);
 * - every transition to `null` (logout, failed refresh, revoked session) purges
 *   the slice so no purchased data of the previous user remains.
 *
 * A failed sync keeps the local list — the store is never wiped on a network error.
 * Mounted once in `App`; renders `null`.
 */
export const PurchasedItinerariesBootstrap: React.FC = () => {
    const persistenceReady = usePersistenceReady();
    const accessToken = usePersistedSliceSelector( authSlice, ( auth ) => auth.accessToken );
    const [ , setPurchasedItineraries ] = usePersistedSlice( purchasedItinerariesSlice );

    /** Previous token value; `undefined` until the first hydrated read. */
    const previousTokenRef = useRef<string | null | undefined>( undefined );

    useEffect( () => {
        if ( !persistenceReady ) {
            return;
        }

        const previous = previousTokenRef.current;
        previousTokenRef.current = accessToken;

        const isAuthenticated = accessToken != null && accessToken !== "";
        const wasAuthenticated = previous != null && previous !== "";

        if ( previous === undefined ) {
            // First hydrated read: boot with an existing session counts as a sync trigger.
            if ( !isAuthenticated ) {
                return;
            }
        } else if ( isAuthenticated === wasAuthenticated ) {
            // No auth transition (token rotation, unrelated renders) — nothing to do.
            return;
        }

        if ( isAuthenticated ) {
            void ( async() => {
                try {
                    const bookings = await TicketPurchaseApi.getPurchaseBookings();
                    setPurchasedItineraries( buildPurchasedEntriesFromServer( bookings ) );
                } catch( error: unknown ) {
                    console.error( "[PurchasedItineraries] Failed to sync purchased itineraries", error );
                }
            } )();

            return;
        }

        setPurchasedItineraries( PURCHASED_ITINERARIES_DEFAULT );
    }, [ accessToken, persistenceReady, setPurchasedItineraries ] );

    return null;
};
